<template>
  <!-- Generic date-range filter for the customer page. Used by the
       date-range filters (`created`, `inactive`, `active`, `noShows`,
       `cancellations`) that previously rendered through the legacy
       `DateRangeOptionalField`. Shape on the wire is unchanged —
       `{ start: number|null, finish: number|null }` of unix seconds. -->
  <UPopover :content="{ side: 'bottom', align: 'start', sideOffset: 6, collisionPadding: 12 }">
    <button
      type="button"
      class="flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 text-left text-sm text-gray-700 shadow-xs transition hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-200 dark:hover:border-gray-700 dark:hover:bg-gray-900"
      :aria-label="rangeLabel"
    >
      <span class="min-w-0 flex-1 truncate">{{ rangeLabel }}</span>
      <UIcon name="i-lucide-calendar-days" class="size-4 shrink-0 text-gray-400 dark:text-gray-500" />
    </button>

    <template #content>
      <div class="w-64 space-y-3 p-3">
        <div class="space-y-1">
          <label class="text-xs font-medium text-gray-600 dark:text-gray-400">
            {{ $t('general.from') }}
          </label>
          <DateField
            v-model="startValue"
            :augmenter="augmenters.timestamp"
            clear
            class="w-full min-w-0"
          />
        </div>

        <div class="space-y-1">
          <label class="text-xs font-medium text-gray-600 dark:text-gray-400">
            {{ $t('general.to') }}
          </label>
          <DateField
            v-model="finishValue"
            :augmenter="augmenters.timestamp"
            clear
            class="w-full min-w-0"
          />
        </div>

        <div v-if="hasValue" class="flex justify-end">
          <button
            type="button"
            class="rounded px-1.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            @click="clear"
          >
            {{ $t('general.clear') }}
          </button>
        </div>
      </div>
    </template>
  </UPopover>
</template>

<script setup lang="ts">
import DateField from '@/components/fields/Date.vue'
import { augmenters } from '@/composables/useFieldAugmenters'

const { t, locale } = useI18n()

// Value shape matches the `DateRangeOptionalField` it replaces — unix
// seconds for both ends, with `null` meaning "no bound on this side".
const modelValue = defineModel<{ start: number | null; finish: number | null }>({
  default: () => ({ start: null, finish: null }),
})

const hasValue = computed(() => !!(modelValue.value.start || modelValue.value.finish))

const startValue = computed({
  get: () => modelValue.value.start,
  set: (value: number | null) => {
    modelValue.value = { ...modelValue.value, start: value }
  },
})

const finishValue = computed({
  get: () => modelValue.value.finish,
  set: (value: number | null) => {
    if (value == null) {
      modelValue.value = { ...modelValue.value, finish: null }
      return
    }

    const finish = new Date(value * 1000)
    finish.setHours(23, 59, 59, 0)
    modelValue.value = { ...modelValue.value, finish: Math.floor(finish.getTime() / 1000) }
  },
})

const formatDate = (ts: number | null): string => {
  if (!ts) return ''
  return new Intl.DateTimeFormat(String(locale.value ?? 'en').replace('_', '-'), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(ts * 1000))
}

const rangeLabel = computed(() => {
  const start = formatDate(modelValue.value.start)
  const finish = formatDate(modelValue.value.finish)

  if (start && finish) return `${start} ${t('general.to').toLowerCase()} ${finish}`
  if (start) return `${t('general.from')} ${start}`
  if (finish) return `${t('general.to')} ${finish}`
  return `${t('general.any')} ${t('general.date').toLowerCase()}`
})

const clear = () => {
  modelValue.value = { start: null, finish: null }
}
</script>
